/**
 * Trust & Compliance Centre — Iteration 118
 *
 * Expanded Security Dashboard. Every readiness % rendered here is
 * computed dynamically by the backend (`/api/security/trust-compliance`)
 * — no hard-coded numbers in either the UI or the response.
 *
 * Sections
 *   1. Security Status (top-line health + last scan / backup / pentest)
 *   2. Data Protection (encryption, MFA, audit, AV, CSP)
 *   3. Compliance readiness (GDPR / ISO 27001 / SOC 2)
 *   4. Evidence pack downloads
 *   5. "Verify Account Security" trigger + recent report
 *
 * Permissions are read from `data.viewer.permissions` — buttons are
 * hidden when the role doesn't have the required capability tag.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck,
  RefreshCcw,
  Activity,
  Database,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  PlayCircle,
} from "lucide-react";
import { toast } from "sonner";

// ----- helpers -----------------------------------------------------------

const STATUS_TONE = {
  pass: { dot: "bg-emerald-500", text: "text-emerald-800", chip: "bg-emerald-100" },
  warn: { dot: "bg-amber-500", text: "text-amber-900", chip: "bg-amber-100" },
  fail: { dot: "bg-rose-500", text: "text-rose-900", chip: "bg-rose-100" },
  manual: { dot: "bg-slate-400", text: "text-slate-700", chip: "bg-slate-100" },
  "n/a": { dot: "bg-slate-300", text: "text-slate-500", chip: "bg-slate-50" },
};

function StatusDot({ status }) {
  const t = STATUS_TONE[status] || STATUS_TONE.manual;
  return <span className={`inline-block w-2 h-2 rounded-full ${t.dot}`} />;
}

function ReadinessBar({ pct }) {
  // Solid bar coloured by the band the backend derived. No fake
  // gradients or "looks-good" defaults — null means "not measurable".
  if (pct == null) {
    return <div className="text-xs text-slate-500 italic">Not measurable yet</div>;
  }
  const band = pct >= 90 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="text-3xl font-semibold text-slate-900 font-mono-data tabular-nums">{pct}%</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">readiness</span>
      </div>
      <div className="mt-2 h-2 rounded-sm bg-slate-100 overflow-hidden">
        <div className={`h-full ${band}`} style={{ width: `${Math.max(2, pct)}%` }} />
      </div>
    </div>
  );
}

function ControlRow({ row, testIdPrefix }) {
  const t = STATUS_TONE[row.status] || STATUS_TONE.manual;
  return (
    <li
      className="py-3 flex items-start gap-3"
      data-testid={`${testIdPrefix}-${row.id}`}
    >
      <StatusDot status={row.status} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-medium text-slate-900">{row.title}</span>
          <Badge className={`${t.chip} ${t.text} border-0 rounded-sm text-[10px] uppercase tracking-wider px-1.5 py-0.5`}>
            {row.status}
          </Badge>
          {(row.standards || []).slice(0, 2).map((s) => (
            <span key={s} className="text-[10px] text-slate-500 font-mono-data">{s}</span>
          ))}
        </div>
        <p className="text-xs text-slate-600 mt-0.5">{row.evidence}</p>
      </div>
    </li>
  );
}

// ----- evidence downloads -----

function EvidenceLink({ kind, label, testId }) {
  const onClick = async () => {
    try {
      const r = await api.get(`/security/evidence/${kind}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cpc-${kind}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${label}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      className="border-slate-200 rounded-sm justify-start text-xs h-auto py-2"
      data-testid={testId}
    >
      <Download className="w-3.5 h-3.5 mr-2" />
      {label}
    </Button>
  );
}

// ----- main page ---------------------------------------------------------

export default function TrustComplianceCentre() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [latestReport, setLatestReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/security/trust-compliance");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSecurityCheck = async () => {
    setRunning(true);
    try {
      const r = await api.post("/security/run-security-check");
      const reportId = r.data?.id;
      toast.success("Security check started — refreshing in 5 seconds…");
      setLatestReport(r.data);
      // Best-effort poll once. Full live polling is out of scope for
      // the dashboard's first cut — the report id is persisted so an
      // operator can refresh manually.
      setTimeout(async () => {
        try {
          const p = await api.get(`/security/security-check/${reportId}`);
          setLatestReport(p.data);
          await load();
        } catch { /* noop */ }
      }, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start security check");
    } finally {
      setRunning(false);
    }
  };

  const runBackupVerify = async () => {
    try {
      const r = await api.post("/security/backup-verify/run");
      toast.success("Backup verification recorded");
      await load();
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  if (loading) return <div className="p-8 text-sm text-slate-600">Loading Trust & Compliance Centre…</div>;
  if (error) return (
    <div className="p-8" data-testid="trust-compliance-error">
      <Card className="p-6 max-w-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <div className="font-semibold text-slate-900 mb-1">Could not load Trust & Compliance Centre</div>
            <div className="text-sm text-slate-700">{error}</div>
            <div className="text-xs mt-3 text-slate-500">
              This view is restricted to staff accounts (super_admin, support, developer, compliance, auditor). Sign in with a staff credential.
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
  if (!data) return null;

  const cats = data.categories || {};
  const sec = cats.security_status || [];
  const dp = cats.data_protection || [];
  const cmp = cats.compliance || [];
  const perms = new Set(data.viewer?.permissions || []);

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto" data-testid="trust-compliance-centre">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-teal-700" />
            Trust &amp; Compliance Centre
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Live, evidence-backed view of every security &amp; compliance control. All readiness percentages are computed from real system state — never hard-coded.
          </p>
          <p className="text-[11px] text-slate-500 mt-2 font-mono-data">
            Generated {data.generated_at?.slice(0, 19)} · Viewing as <strong>{data.viewer?.role}</strong> · {data.viewer?.permissions?.length} permission(s)
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={load}
            className="border-slate-200 rounded-sm"
            data-testid="trust-compliance-refresh"
          >
            <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          {perms.has("security_check:run") && (
            <Button
              onClick={runSecurityCheck}
              disabled={running}
              className="bg-teal-700 hover:bg-teal-800 text-white rounded-sm"
              data-testid="trust-compliance-run-security-check"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              {running ? "Starting…" : "Run Security Check"}
            </Button>
          )}
        </div>
      </div>

      {/* Top-line readiness tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5" data-testid="trust-tile-security">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
            <Activity className="w-3.5 h-3.5" /> Security status
          </div>
          <div className="mt-2"><ReadinessBar pct={data.summary?.security_status_pct} /></div>
        </Card>
        <Card className="p-5" data-testid="trust-tile-data">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
            <Database className="w-3.5 h-3.5" /> Data protection
          </div>
          <div className="mt-2"><ReadinessBar pct={data.summary?.data_protection_pct} /></div>
        </Card>
        <Card className="p-5" data-testid="trust-tile-compliance">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
            <FileText className="w-3.5 h-3.5" /> Compliance — ISO 27001
          </div>
          <div className="mt-2"><ReadinessBar pct={data.summary?.iso27001_readiness_pct} /></div>
          <div className="text-[11px] text-slate-500 mt-3 grid grid-cols-2 gap-1">
            <div>GDPR <span className="font-mono-data text-slate-700">{data.summary?.gdpr_readiness_pct ?? "—"}%</span></div>
            <div>SOC 2 <span className="font-mono-data text-slate-700">{data.summary?.soc2_readiness_pct ?? "—"}%</span></div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-5" data-testid="trust-section-security-status">
          <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4" /> Security Status
          </h3>
          <ul className="divide-y divide-slate-100">
            {sec.map((r) => <ControlRow row={r} key={r.id} testIdPrefix="trust-ctrl-sec" />)}
          </ul>
        </Card>

        <Card className="p-5" data-testid="trust-section-data-protection">
          <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Database className="w-4 h-4" /> Data Protection
          </h3>
          <ul className="divide-y divide-slate-100">
            {dp.map((r) => <ControlRow row={r} key={r.id} testIdPrefix="trust-ctrl-dp" />)}
          </ul>
        </Card>
      </div>

      <Card className="p-5 mb-6" data-testid="trust-section-compliance">
        <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Compliance Readiness
        </h3>
        <ul className="divide-y divide-slate-100">
          {cmp.map((r) => <ControlRow row={r} key={r.id} testIdPrefix="trust-ctrl-cmp" />)}
        </ul>
      </Card>

      {/* Evidence pack */}
      {perms.has("evidence:download") && (
        <Card className="p-5 mb-6" data-testid="trust-section-evidence">
          <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Evidence pack
          </h3>
          <p className="text-xs text-slate-600 mb-3">
            Timestamped PDF exports for auditor review. Each download writes an audit_log row tying the export to your account.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <EvidenceLink kind="security_report" label="Security Report" testId="trust-evidence-security" />
            <EvidenceLink kind="audit_report" label="Audit Report" testId="trust-evidence-audit" />
            <EvidenceLink kind="backup_report" label="Backup Verification" testId="trust-evidence-backup" />
            <EvidenceLink kind="compliance_pack" label="Compliance Pack" testId="trust-evidence-compliance" />
          </div>
        </Card>
      )}

      {/* Run backup verify */}
      {perms.has("backup_verify:run") && (
        <Card className="p-5 mb-6" data-testid="trust-section-backup-actions">
          <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Quarterly backup verification
          </h3>
          <p className="text-xs text-slate-600 mb-3">
            Records the outcome of a quarterly restore drill. The drill itself runs out-of-band against a non-production cluster — this button persists the operator-confirmed evidence row.
          </p>
          <Button
            variant="outline"
            onClick={runBackupVerify}
            className="border-slate-200 rounded-sm"
            data-testid="trust-backup-verify-run"
          >
            <PlayCircle className="w-4 h-4 mr-2" /> Record backup verification
          </Button>
        </Card>
      )}

      {latestReport && (
        <Card className="p-5 mb-6" data-testid="trust-latest-report">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">Latest security check</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-slate-700">
            <div><div className="text-[10px] uppercase text-slate-500">Status</div><div className="font-mono-data">{latestReport.status}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Files scanned</div><div className="font-mono-data">{latestReport.summary?.files_scanned ?? 0}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Clean</div><div className="font-mono-data text-emerald-700">{latestReport.summary?.clean ?? 0}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Infected</div><div className="font-mono-data text-rose-700">{latestReport.summary?.infected ?? 0}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Inaccessible</div><div className="font-mono-data text-amber-700">{latestReport.summary?.inaccessible ?? 0}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Integrity changed</div><div className="font-mono-data text-amber-700">{latestReport.summary?.integrity_changed ?? 0}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Engine</div><div className="font-mono-data">{latestReport.engine}</div></div>
            <div><div className="text-[10px] uppercase text-slate-500">Duration</div><div className="font-mono-data">{latestReport.duration_seconds ?? "—"} s</div></div>
          </div>
        </Card>
      )}

      <div className="text-[11px] text-slate-500 mt-8">
        Every action on this page (view, run, download) writes an audit_log row keyed to your staff id and reason. Access Control Policy v77.99.118.
      </div>
    </div>
  );
}
